import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Icon, withStyles, BodyText } from 'vopay';
import { Title, CircleButton, Button } from 'UI';
import styles from './EnhancedVerificationDialog.module.scss';
import Feature from './Feature';
import { useTranslation } from 'react-i18next';

type EnhancedVerificationDialogProps = {
  onCancel: () => void;
};

const cn = withStyles(styles);

const EnhancedVerificationDialog: FC<EnhancedVerificationDialogProps> = ({ onCancel }) => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'InteracVerification.EnhancedVerificationDialog' });

  return (
    <Card className={cn('enhancedVerificationDialog')}>
      <Title level="3">{t('title', { defaultValue: 'Enhanced Verification' })}</Title>

      <div className={cn('closeButton')}>
        <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
      </div>

      <BodyText className={cn('description')}>
        {t('desc', {
          defaultValue: 'Confirm ownership and registration details retrieved from Interac for this contact.',
        })}
      </BodyText>

      <div className={cn('features')}>
        <Feature
          icon={<Icon icon="badgeBlueGradient" width="35px" height="35px" color="none" />}
          title={t('RegisteredInteracName.title', { defaultValue: 'Registered Interac Name' })}
          desc={t('RegisteredInteracName.desc', {
            defaultValue: 'See the registered name tied to the Interac account.',
          })}
        />

        <Feature
          icon={<Icon icon="alternativeEmailGradient" width="35px" height="35px" color="none" />}
          title={t('Alias.title', { defaultValue: 'Alias' })}
          desc={t('Alias.desc', { defaultValue: 'Identify the associated alias.' })}
        />

        <Feature
          icon={<Icon icon="infoBlueGradient" width="35px" height="35px" color="none" />}
          title={t('AccountType.title', { defaultValue: 'Account Type' })}
          desc={t('AccountType.desc', { defaultValue: 'Know whether the account is personal or business.' })}
        />

        <Feature
          icon={<Icon icon="moveToInboxBlueGradient" width="35px" height="35px" color="none" />}
          title={t('AutoDepositStatus.title', { defaultValue: 'Auto-Deposit Status' })}
          desc={t('AutoDepositStatus.desc', { defaultValue: 'Check if incoming funds are automatically accepted.' })}
        />

        <Feature
          icon={<Icon icon="swapHorizontalCircleBlueGradient" width="35px" height="35px" color="none" />}
          title={t('TransactionLimit.title', { defaultValue: 'Transaction Limit' })}
          desc={t('TransactionLimit.desc', { defaultValue: 'View the maximum amount allowed per transaction.' })}
        />
      </div>

      <Button
        className={cn('button')}
        colorScheme="primary"
        onClick={() => {
          onCancel();

          history.push({
            pathname: '/create-tickets',
            state: { ticketSubject: 'Enquiry about Enhanced Interac email verification' },
          });
        }}
      >
        {t('contactButton', { defaultValue: 'Contact Support' })}
      </Button>
    </Card>
  );
};

export default EnhancedVerificationDialog;
